import { useState } from "react";
import { Calculator, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";

const DEFAULTS = {
  cameras: 64,
  resolution: "4MP",
  fps: 15,
  retentionDays: 30,
  compression: "h265",
  radarSensors: 2,
  accessReaders: 12,
};
const FPS_MIN = 1;
const FPS_MAX = 60;
const RETENTION_MIN_DAYS = 1;
const RETENTION_MAX_DAYS = 365;
const CAMERAS_MIN = 1;
const CAMERAS_MAX = 10000;

export default function CalculatorPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [form, setForm] = useState({
    cameras: DEFAULTS.cameras,
    resolution: DEFAULTS.resolution,
    fps: DEFAULTS.fps,
    retention_days: DEFAULTS.retentionDays,
    compression: DEFAULTS.compression,
    radar_sensors: DEFAULTS.radarSensors,
    access_readers: DEFAULTS.accessReaders,
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const premium = ["admin", "staff", "client_premium"].includes(user?.role);

  const compute = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      ["cameras", "fps", "retention_days", "radar_sensors", "access_readers"].forEach(k => payload[k] = Number(payload[k]));
      const { data } = await api.post("/calculator", payload);
      setResult(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-10 space-y-8 max-w-6xl">
      <header>
        <div className="text-overline flex items-center gap-2">05 · Calculator <Crown className="w-3 h-3 text-primary" /> PREMIUM</div>
        <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight text-white mt-2" data-testid="calc-title">{t("calc.title")}</h1>
        <p className="mt-2 text-sm text-secondary-foreground/70">{t("calc.subtitle")}</p>
      </header>

      {!premium && (
        <div className="p-5 border border-primary/30 bg-primary/5 rounded-sm text-sm text-white font-mono tracking-widest uppercase text-[11px]" data-testid="calc-gate-notice">
          {t("calc.gated")}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={compute} className="p-6 border border-border rounded-sm bg-card space-y-4" data-testid="calc-form">
          <div className="grid grid-cols-2 gap-3">
            <F label={t("calc.cameras")}><input type="number" min={CAMERAS_MIN} max={CAMERAS_MAX} value={form.cameras} onChange={e => setForm({ ...form, cameras: e.target.value })} className={inp} data-testid="calc-cameras-input" /></F>
            <F label={t("calc.resolution")}><select value={form.resolution} onChange={e => setForm({ ...form, resolution: e.target.value })} className={inp} data-testid="calc-resolution-select">{["1080p","4MP","4K"].map(r => <option key={r}>{r}</option>)}</select></F>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <F label={t("calc.fps")}><input type="number" min={FPS_MIN} max={FPS_MAX} value={form.fps} onChange={e => setForm({ ...form, fps: e.target.value })} className={inp} data-testid="calc-fps-input" /></F>
            <F label={t("calc.retention")}><input type="number" min={RETENTION_MIN_DAYS} max={RETENTION_MAX_DAYS} value={form.retention_days} onChange={e => setForm({ ...form, retention_days: e.target.value })} className={inp} data-testid="calc-retention-input" /></F>
            <F label={t("calc.compression")}><select value={form.compression} onChange={e => setForm({ ...form, compression: e.target.value })} className={inp} data-testid="calc-compression-select">{["h264","h265"].map(c => <option key={c}>{c}</option>)}</select></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label={t("calc.radar")}><input type="number" min={0} value={form.radar_sensors} onChange={e => setForm({ ...form, radar_sensors: e.target.value })} className={inp} data-testid="calc-radar-input" /></F>
            <F label={t("calc.readers")}><input type="number" min={0} value={form.access_readers} onChange={e => setForm({ ...form, access_readers: e.target.value })} className={inp} data-testid="calc-readers-input" /></F>
          </div>
          <button type="submit" disabled={busy || !premium} className="w-full h-11 rounded-sm bg-primary text-primary-foreground font-medium hover:bg-[#33B9FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2" data-testid="calc-compute-button" aria-disabled={!premium}>
            <Calculator className="w-4 h-4" /> {busy ? "…" : t("calc.compute")}
          </button>
        </form>

        <div className="p-6 border border-border rounded-sm bg-card" data-testid="calc-output">
          <div className="text-overline mb-5">OUTPUT</div>
          {!result ? (
            <div className="text-sm text-muted-foreground font-mono">Waiting for computation…</div>
          ) : (
            <div className="space-y-5">
              <Metric label={t("calc.out.bandwidth")} value={`${result.bandwidth_total_mbps} Mbps`} sub={`${result.bandwidth_total_gbps} Gbps · ${result.bandwidth_per_stream_mbps} Mbps/stream`} testId="calc-out-bandwidth" />
              <Metric label={t("calc.out.storage")} value={`${result.storage_total_tb} TB`} sub={`${result.storage_per_camera_gb_per_day} GB/cam/day`} testId="calc-out-storage" />
              <Metric label={t("calc.out.power")} value={`${result.power_total_kw} kW`} sub={`${result.power_total_w} W total draw`} testId="calc-out-power" />
              <Metric label={t("calc.out.switches")} value={result.recommended_poe_switches_24p} sub={`UPS runtime ≈ ${result.estimated_ups_runtime_min_at_80pct} min @ 80% load`} testId="calc-out-switches" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const inp = "w-full h-10 px-3 rounded-sm bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none font-mono text-sm text-white";
function F({ label, children }) { return <label className="block"><div className="mb-1.5 text-[10px] font-mono tracking-[0.22em] text-muted-foreground uppercase">{label}</div>{children}</label>; }
function Metric({ label, value, sub, testId }) {
  return (
    <div className="border-b border-border pb-4 last:border-b-0 last:pb-0" data-testid={testId}>
      <div className="text-[10px] font-mono tracking-[0.22em] text-muted-foreground uppercase">{label}</div>
      <div className="mt-1.5 text-3xl font-semibold font-mono text-primary tracking-tight">{value}</div>
      <div className="text-[11px] font-mono tracking-widest text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}
